import * as React from "react";
import { LocalTrack, Room, TwilioError } from "twilio-video";
import { makeLogger } from "../../utils/logger";
import { getAccessToken } from "../helpers/twilioHelpers";
import { useSnackbar } from "notistack";
import { getIntl } from "../../localization/intl";

const log = makeLogger("Twilio - useAutoJoinRoom");

const connectionErrors: number[] = [53000, 53105, 53106, 53405, 53001, 53002, 53205];

export function useAutoJoinRoom(
	roomId: string,
	authToken: string | undefined,
	twilioRoom: Room | null,
	getAudioAndVideoTracks: () => Promise<LocalTrack[] | undefined>,
	connectTwilio: (accessToken: string, localTracks: LocalTrack[] | undefined) => Promise<Room | undefined>,
	acquireLocalTracks: boolean,
	isEnabled: boolean,
	onError: (err: TwilioError) => void,
) {
	const { enqueueSnackbar } = useSnackbar();

	React.useEffect(() => {
		if (twilioRoom || !roomId) return;

		if (!isEnabled) {
			return;
		}

		const setup = async () => {
			log.debug("Connecting", roomId);

			let localTracks: LocalTrack[] | undefined;
			if (acquireLocalTracks) {
				try {
					localTracks = await getAudioAndVideoTracks();
				} catch (err) {
					onError(err);
				}
			}

			try {
				const {
					data: { token: accessToken },
				} = await getAccessToken(roomId, authToken);

				await connectTwilio(accessToken, localTracks);
				log.debug("Connected", accessToken);
			} catch (err) {
				onError(err);
				if (connectionErrors.includes(err.code)) {
					enqueueSnackbar(getIntl().formatMessage({ id: `TWILIO.error.${err.code}` }), {
						variant: "error",
						persist: true,
					});
				}
			}
		};

		setup();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		authToken,
		roomId,
		twilioRoom,
		connectTwilio,
		getAudioAndVideoTracks,
		onError,
		acquireLocalTracks,
		enqueueSnackbar,
	]);

	React.useEffect(() => {
		if (!twilioRoom) {
			return;
		}
		return () => {
			twilioRoom.localParticipant?.videoTracks?.forEach((publication) => {
				publication.track.stop();
				publication.unpublish();
			});
			twilioRoom.localParticipant?.audioTracks?.forEach((publication) => {
				publication.track.stop();
				publication.unpublish();
			});
			twilioRoom.removeAllListeners();
			twilioRoom.disconnect();
			log.debug("Disconnected");
		};
	}, [twilioRoom]);
}
